import { ExperimentService, ExperimentWorkflow, ExperimentAssignment, ExperimentResult } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.min.css'; // Optional Theme applied to the grid
import { useState } from 'react';
// import Link from 'next/link';
import { $router } from '@/stores/router';  // Import the router store directly

export type ExperimentPageProps = {
  experimentId: string;
  organizationId: string;
};

interface MarkdownTextProps {
  text: string;
}

export const ExperimentPage = ({
  experimentId,
  organizationId,
}: ExperimentPageProps) => {
  const [isOpen, setOpen] = useState(false);
  // get the current workspace

  // get the current organization

  const org = useStore($org);
  // get the current project
  const ex = useStore($experiment);
  // breadcrumb pages

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/experiments`
        : '/organizations',
    },
    {
      label: ex?.name || 'Experiment',
      href: `/organization/${org?.id}/experiments/${experimentId}`,
    },
  ];

  const { theme } = useTheme();

  // get the account receivable
  // need to create controller for this
  const experimentResult = useQuery({
    queryKey: ['experimentResult', organizationId, experimentId],
    queryFn: async () => {
      const result = await ExperimentService.getExperiment(organizationId, experimentId);

      if (!result) {
        throw new Error('No data found');
      }
      return result;
    },
  });

  const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
    // Function to convert markdown-like text to HTML
    const convertToHTML = (text: string): string => {
      // Replace '**Header**' with HTML <h1> or <h2> tags
      const formattedText = text
        .replace(/\*\*(.*?)\*\*/g, (_, header) => `<h2>${header}</h2>`) // Change <h2> to <h1> for main headers if needed
        .replace(/\n/g, '<br/>'); // Replace line breaks with <br> tags

      return formattedText;
    };

    // Convert text to HTML
    const htmlText = convertToHTML(text);

    return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {/* Page Header */}
      <div className="mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {experimentResult.data?.name || 'Experiment details'}
          </h2>
          {experimentResult.data?.createdAtUtc && (
            <p className="text-muted-foreground">
              Created on{' '}
              <time dateTime={experimentResult.data.createdAtUtc}>
                {new Date(experimentResult.data.createdAtUtc).toLocaleDateString()}
              </time>
            </p>
          )}
        </div>
      </div>
      {/* Ex detail */}
      <div className="mt-4">
        <>
          <div className={cn('relative mt-4 rounded-md border p-4')}>
            <div className="flex items-center justify-between">
              <h2 className={cn('mb-2 text-lg font-semibold')}>
                Experiment Details
              </h2>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {/* Smart analyze and show/hide columns button used to exist here
                    Figure out what actions to add */}
                </div>

                <div className="relative">
                  {/* Download button used to exist here
                    Figure out what actions to add */}
                </div>
              </div>
            </div>
            <div className={cn('mt-4 grid grid-cols-2 gap-4')}>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Experiment Name</p>
                <p className={cn('text-xl')}>
                  {experimentResult.data?.name}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Start Date</p>
                <p className={cn('text-xl')}>
                {experimentResult.data?.startDate &&
                    new Date(
                      experimentResult.data.startDate,
                    ).toLocaleDateString()}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>End Date</p>
                <p className={cn('text-xl')}>
                {experimentResult.data?.endDate &&
                    new Date(
                      experimentResult.data.endDate,
                    ).toLocaleDateString()}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Workflows</p>
                <div className="space-y-2">
                  {experimentResult.data?.workflows?.map((workflow: ExperimentWorkflow, index: number) => (
                    <div key={workflow.workflowId} className="flex items-center justify-between">
                      <a 
                        href={`/organization/${org?.id}/workflows_o/${workflow.workflow?.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          openPage($router, 'workflow_o', {
                            organizationId: org?.id || '',
                            workflowId: workflow.workflow?.id || ''
                          });
                        }} 
                        className="hover:underline"
                      >
                        Workflow {index + 1}: {workflow.workflow?.name}
                      </a>
                      <p className={cn('text-sm text-muted-foreground')}>
                        Weight: {workflow.weight}
                        Variant Name: {workflow.variantName}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Assignments</p>
                <div className="space-y-2">
                  {experimentResult.data?.assignments?.length ? (
                    experimentResult.data.assignments.map((assignment: ExperimentAssignment, index: number) => (
                      <div key={assignment.workflowId} className="flex items-center justify-between">
                        <p className={cn('text-sm')}>
                        Assignment {index + 1}: {assignment.id}
                      </p>
                      <p className={cn('text-sm text-muted-foreground')}>
                        Account: {assignment.accountId}
                        Assigned Date: {assignment.assignedDate}
                      </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No assignments available
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Results</p>
                <div className="space-y-2">
                  {experimentResult.data?.results?.length ? (
                    experimentResult.data.results.map((result: ExperimentResult, index: number) => (
                      <div key={result.workflowId} className="flex items-center justify-between">
                        <p className={cn('text-sm')}>
                          Result {index + 1}: {result.id}
                        </p>
                        <p className={cn('text-sm text-muted-foreground')}>
                          Account: {result.accountId}
                          Status: {result.status}
                          Result Date: {result.resultDate}
                          Amount Collected: {result.amountCollected}
                          Interaction Details: {result.interactionDetails}    
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No results available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div></div>
        </>
      </div>
    </div>
  );
};
